import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clinicalApi, { ClinicalApi, Study, StudyPage, viewerURL } from '../services/clinicalApi';
import './clinical.css';

export function ErrorMessage({ message }: { message: string }) {
  return message ? (
    <p
      role="alert"
      className="clinical-error"
      data-cy="clinical-error"
    >
      {message}
    </p>
  ) : null;
}
export function errorText(error: unknown): string {
  return error instanceof Error && error.name !== 'SyntaxError' && error.name !== 'TypeError'
    ? error.message
    : '服务暂不可用，请重试。';
}
export function statusText(state?: string) {
  return (
    { '0': '待检查', '1': '待诊断', '2': '已诊断', '3': '已审核', '4': '已审核', '5': '已审核' }[
      state || ''
    ] || '状态未知'
  );
}
export function Pagination({
  page,
  total,
  busy,
  onPage,
}: {
  page: number;
  total: number;
  busy: boolean;
  onPage: (page: number) => void;
}) {
  return (
    <nav
      aria-label="检查分页"
      className="clinical-pagination"
    >
      <button
        disabled={busy || page <= 1}
        onClick={() => onPage(page - 1)}
        data-cy="clinical-prev"
      >
        上一页
      </button>
      <span>
        第 {page} 页 · 共 {total} 条
      </span>
      <button
        disabled={busy || page * 20 >= total}
        onClick={() => onPage(page + 1)}
        data-cy="clinical-next"
      >
        下一页
      </button>
    </nav>
  );
}
export function ReportView({
  registerKey,
  api = clinicalApi,
}: {
  registerKey: string;
  api?: ClinicalApi;
}) {
  const [retry, setRetry] = useState(0);
  const resourceKey = JSON.stringify([registerKey, retry]);
  const [resource, setResource] = useState({
    key: '',
    api: null as ClinicalApi | null,
    url: '',
    error: '',
  });
  useEffect(() => {
    const controller = new AbortController();
    let objectURL = '';
    api
      .report(registerKey, controller.signal)
      .then(blob => {
        if (controller.signal.aborted) {
          return;
        }
        objectURL = URL.createObjectURL(blob);
        setResource({ key: resourceKey, api, url: objectURL, error: '' });
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          setResource({ key: resourceKey, api, url: '', error: errorText(error) });
        }
      });
    return () => {
      controller.abort();
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [registerKey, api, resourceKey]);
  const active =
    resource.key === resourceKey && resource.api === api ? resource : { url: '', error: '' };
  return (
    <div
      className="clinical-report"
      data-cy="clinical-report"
    >
      {active.error ? (
        <>
          <ErrorMessage message={active.error} />
          <button onClick={() => setRetry(retry + 1)}>重试报告</button>
        </>
      ) : active.url ? (
        <iframe
          title="检查报告（只读）"
          src={active.url}
          data-cy="clinical-report-pdf"
        />
      ) : (
        <p role="status">正在获取报告…</p>
      )}
    </div>
  );
}
export function HistoryView({ study }: { study: Study }) {
  return (
    <HistoryContent
      key={JSON.stringify([study.patient_id, study.register_key])}
      study={study}
    />
  );
}
function HistoryContent({ study }: { study: Study }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const queryKey = JSON.stringify([study.patient_id, page, retry]);
  const [response, setResponse] = useState({
    key: '',
    value: { studies: [], total: 0 } as StudyPage,
    error: '',
  });
  const busy = response.key !== queryKey;
  const result = busy ? { studies: [], total: 0 } : response.value;
  const queryError = busy ? '' : response.error;
  const [error, setError] = useState('');
  const [opening, setOpening] = useState('');
  const openRequest = useRef<AbortController | null>(null);
  useEffect(() => () => openRequest.current?.abort(), [study.register_key]);
  useEffect(() => {
    const controller = new AbortController();
    clinicalApi
      .history(study.patient_id, page, controller.signal)
      .then(value => {
        if (!controller.signal.aborted) {
          setResponse({ key: queryKey, value, error: '' });
        }
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          setResponse({ key: queryKey, value: { studies: [], total: 0 }, error: errorText(error) });
        }
      });
    return () => controller.abort();
  }, [study.patient_id, page, queryKey]);
  async function open(record: Study) {
    openRequest.current?.abort();
    const controller = new AbortController();
    openRequest.current = controller;
    setOpening(record.register_key);
    setError('');
    try {
      const uids = await clinicalApi.uids(record, controller.signal);
      if (!controller.signal.aborted) {
        navigate(viewerURL(record, uids));
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setError(errorText(error));
      }
    }
    if (!controller.signal.aborted) {
      setOpening('');
    }
  }
  return (
    <div data-cy="clinical-history">
      <h3>历史检查</h3>
      <ErrorMessage message={error || queryError} />
      {(error || queryError) && (
        <button
          onClick={() => {
            setRetry(retry + 1);
            setError('');
          }}
        >
          重试历史查询
        </button>
      )}
      {busy ? (
        <p role="status">正在查询历史检查…</p>
      ) : !error && !queryError && result.studies.length === 0 ? (
        <p>暂无历史检查</p>
      ) : null}
      <ul className="clinical-record-list">
        {result.studies.map(record => (
          <li key={record.register_key}>
            <span>
              {record.study_time || record.register_time || '时间未知'} · {record.modality_code}{' '}
              {record.bodypart_name}
            </span>
            <button
              disabled={!!opening || record.register_key === study.register_key}
              onClick={() => open(record)}
            >
              {record.register_key === study.register_key
                ? '当前检查'
                : opening === record.register_key
                  ? '正在打开…'
                  : '查看影像'}
            </button>
          </li>
        ))}
      </ul>
      <Pagination
        page={page}
        total={result.total}
        busy={busy || !!opening}
        onPage={next => {
          setPage(next);
          setError('');
        }}
      />
    </div>
  );
}
export function RecordPanel() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const study: Study = {
    register_key: query.get('registerKey') || '',
    patient_id: query.get('patientId') || '',
  };
  const [tab, setTab] = useState('report');
  return (
    <section
      className="clinical-panel"
      data-cy="clinical-record-panel"
    >
      <div
        role="tablist"
        aria-label="检查资料"
        className="clinical-tabs"
      >
        <button
          role="tab"
          aria-selected={tab === 'report'}
          onClick={() => setTab('report')}
        >
          报告
        </button>
        <button
          role="tab"
          aria-selected={tab === 'history'}
          onClick={() => setTab('history')}
        >
          历史检查
        </button>
      </div>
      <div role="tabpanel">
        {tab === 'report' ? (
          <ReportView registerKey={study.register_key} />
        ) : (
          <HistoryView study={study} />
        )}
      </div>
    </section>
  );
}
